import React from "react";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Select from "@cloudscape-design/components/select";
import DatePicker from "@cloudscape-design/components/date-picker";
import Container from "@cloudscape-design/components/container";

const PERIOD_OPTIONS = [
	{ value: "all", label: "All time" },
	{ value: "day", label: "Today" },
	{ value: "week", label: "Last 7 days" },
	{ value: "month", label: "Last 30 days" },
];

const GROUP_BY_OPTIONS = [
	{ value: "day", label: "Day" },
	{ value: "reason", label: "Reason" },
	{ value: "operator", label: "Employee" },
];

const PAGE_SIZE_OPTIONS = [
	{ value: 20, label: "20" },
	{ value: 50, label: "50" },
	{ value: 100, label: "100" },
];

const GROUP_BY_LABELS = GROUP_BY_OPTIONS.reduce((labels, option) => {
	labels[option.value] = option.label;

	return labels;
}, {});

const METRIC_GRID_STYLE = {
	display: "grid",
	gap: "8px",
	gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
};

const METRIC_CARD_STYLE = {
	background: "var(--color-background-container-content-o88wxm, #ffffff)",
	border: "1px solid var(--color-border-container-top-j8egqz, #eaeded)",
	borderRadius: "12px",
	boxShadow: "0 1px 4px rgba(0, 0, 0, 0.14)",
	boxSizing: "border-box",
	minHeight: "58px",
	padding: "10px 14px",
};

const METRIC_LABEL_STYLE = {
	fontSize: "12px",
	fontWeight: 700,
	lineHeight: "16px",
	marginBottom: "6px",
};

const METRIC_VALUE_STYLE = {
	fontSize: "20px",
	fontWeight: 700,
	lineHeight: "24px",
};

const emptyAnalytics = () => ({
	checks: { total: 0, success: 0, fail: 0 },
	grants: { success: 0 },
	uniquePlayers: 0,
	breakdown: [],
});

const padDatePart = (value) => String(value).padStart(2, "0");

const formatDateInput = (date) => [date.getFullYear(), padDatePart(date.getMonth() + 1), padDatePart(date.getDate())].join("-");

const addDays = (date, days) => {
	const result = new Date(date);
	result.setDate(result.getDate() + days);
	return result;
};

const getPeriodRange = (period) => {
	const today = new Date();

	switch (period) {
		case "all":
			return {
				dateFrom: "",
				dateTo: "",
			};
		case "week":
			return {
				dateFrom: formatDateInput(addDays(today, -6)),
				dateTo: formatDateInput(today),
			};
		case "month":
			return {
				dateFrom: formatDateInput(addDays(today, -29)),
				dateTo: formatDateInput(today),
			};
		case "day":
		default:
			return {
				dateFrom: formatDateInput(today),
				dateTo: formatDateInput(today),
			};
	}
};

const defaultFilters = () => {
	const range = getPeriodRange("all");

	return {
		period: "all",
		dateFrom: range.dateFrom,
		dateTo: range.dateTo,
		groupBy: "day",
	};
};

const isBlank = (value) => value === null || typeof value === "undefined" || value === "";

const toNumber = (value) => {
	const parsed = Number(value || 0);
	return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value) => toNumber(value).toLocaleString();

const displayValue = (value) => (isBlank(value) ? "-" : String(value));

const normalizeAnalytics = (response = {}) => {
	const checks = response.checks || {};
	const grants = response.grants || {};

	return {
		checks: {
			total: toNumber(checks.total),
			success: toNumber(checks.success),
			fail: toNumber(checks.fail),
		},
		grants: {
			success: toNumber(grants.success),
		},
		uniquePlayers: toNumber(response.uniquePlayers),
		breakdown: Array.isArray(response.breakdown)
			? response.breakdown.map((item) => ({
					group: isBlank(item.group) ? "" : String(item.group),
					checks: {
						success: toNumber(item.checks?.success),
						fail: toNumber(item.checks?.fail),
					},
					grants: {
						success: toNumber(item.grants?.success),
						failed: toNumber(item.grants?.failed),
					},
					uniquePlayers: toNumber(item.uniquePlayers),
			  }))
			: [],
	};
};

class VipFreespinsAnalytics extends React.Component {
	constructor(props) {
		super(props);

		const filters = defaultFilters();
		const breakdownPerPage = Number(localStorage.getItem("freespinsAnalytics.breakdownPerPage")) || 50;

		this.state = {
			loaded: false,
			data: emptyAnalytics(),
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
			filters,
			appliedFilters: filters,
			breakdownPage: 1,
			breakdownPerPage,
		};
	}

	componentDidMount() {
		this.loadAnalytics();
	}

	componentWillUnmount() {
		clearTimeout(this.filterApplyTimer);
	}

	getSelectedOption(options, value) {
		return options.find((option) => option.value === value) || null;
	}

	buildRequestParams(filters) {
		const params = {
			groupBy: filters.groupBy,
		};

		if (filters.dateFrom) params.dateFrom = `${filters.dateFrom} 00:00:00`;
		if (filters.dateTo) params.dateTo = `${filters.dateTo} 23:59:59`;

		return params;
	}

	loadAnalytics = (options = {}) => {
		const filters = options.filters || this.state.appliedFilters;

		this.setState({
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		this.props.Interface.loadFreespinsAnalytics(this.buildRequestParams(filters))
			.then((response) => {
				this.setState({
					loaded: true,
					data: normalizeAnalytics(response),
					appliedFilters: filters,
					breakdownPage: 1,
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
			})
			.catch((error) => {
				this.setState({
					loaded: true,
					data: emptyAnalytics(),
					error,
				});
			});
	};

	scheduleApplyFilters(delay = 0) {
		clearTimeout(this.filterApplyTimer);
		this.filterApplyTimer = setTimeout(() => {
			this.filterApplyTimer = null;
			this.applyFilters();
		}, delay);
	}

	updateFilter = (field, value) => {
		const applyDelay = field === "dateFrom" || field === "dateTo" ? 400 : 0;

		this.setState((state) => ({
			filters: {
				...state.filters,
				[field]: value,
			},
		}), () => this.scheduleApplyFilters(applyDelay));
	};

	updatePeriod = (period) => {
		const range = getPeriodRange(period);

		this.setState((state) => ({
			filters: {
				...state.filters,
				period,
				dateFrom: range.dateFrom,
				dateTo: range.dateTo,
			},
		}), () => this.scheduleApplyFilters());
	};

	applyFilters = () => {
		const filters = {
			...this.state.filters,
		};

		this.setState(
			{
				filters,
				appliedFilters: filters,
				breakdownPage: 1,
			},
			() => this.loadAnalytics({ filters })
		);
	};

	clearFilters = () => {
		clearTimeout(this.filterApplyTimer);

		const filters = defaultFilters();

		this.setState(
			{
				filters,
				appliedFilters: filters,
				breakdownPage: 1,
			},
			() => this.loadAnalytics({ filters })
		);
	};

	renderMetric(title, value) {
		return (
			<div style={METRIC_CARD_STYLE}>
				<div style={METRIC_LABEL_STYLE}>{title}</div>
				<div style={METRIC_VALUE_STYLE}>{value}</div>
			</div>
		);
	}

	renderFilters() {
		const { filters, loaded } = this.state;

		return (
			<Container disableContentPaddings>
				<div style={{ padding: "12px 16px 14px" }}>
					<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
						<div>
							<div style={{ fontSize: "20px", fontWeight: 700, lineHeight: "24px", marginBottom: "8px" }}>Filters</div>
							<div style={{ display: "flex", alignItems: "flex-end", gap: "8px", flexWrap: "wrap" }}>
								<div style={{ width: "150px" }}>
									<FormField label="Period" stretch>
										<Select
											disabled={!loaded}
											selectedOption={this.getSelectedOption(PERIOD_OPTIONS, filters.period)}
											onChange={({ detail }) => this.updatePeriod(detail.selectedOption.value)}
											options={PERIOD_OPTIONS}
										/>
									</FormField>
								</div>
								<div style={{ width: "150px" }}>
									<FormField label="Breakdown by" stretch>
										<Select
											disabled={!loaded}
											selectedOption={this.getSelectedOption(GROUP_BY_OPTIONS, filters.groupBy)}
											onChange={({ detail }) => this.updateFilter("groupBy", detail.selectedOption.value)}
											options={GROUP_BY_OPTIONS}
										/>
									</FormField>
								</div>
							</div>
						</div>
						<SpaceBetween direction="horizontal" size="xs">
							<FormField label={<span style={{ visibility: "hidden" }}>Actions</span>}>
								<Button disabled={!loaded} onClick={this.clearFilters}>
									Clear
								</Button>
							</FormField>
							<div style={{ width: "150px" }}>
								<FormField label="Date From" stretch>
									<DatePicker
										disabled={!loaded}
										value={filters.dateFrom}
										onChange={({ detail }) => this.updateFilter("dateFrom", detail.value)}
										placeholder="YYYY-MM-DD"
									/>
								</FormField>
							</div>
							<div style={{ width: "150px" }}>
								<FormField label="Date To" stretch>
									<DatePicker
										disabled={!loaded}
										value={filters.dateTo}
										onChange={({ detail }) => this.updateFilter("dateTo", detail.value)}
										placeholder="YYYY-MM-DD"
									/>
								</FormField>
							</div>
						</SpaceBetween>
					</div>
				</div>
			</Container>
		);
	}

	renderTotals() {
		const { data } = this.state;

		return (
			<div style={METRIC_GRID_STYLE}>
				{this.renderMetric("Checks total", formatNumber(data.checks.total))}
				{this.renderMetric("Checks success", formatNumber(data.checks.success))}
				{this.renderMetric("Checks fail", formatNumber(data.checks.fail))}
				{this.renderMetric("Grants success", formatNumber(data.grants.success))}
				{this.renderMetric("Unique players", formatNumber(data.uniquePlayers))}
			</div>
		);
	}

	getBreakdownColumns() {
		const { appliedFilters } = this.state;
		const groupByLabel = GROUP_BY_LABELS[appliedFilters.groupBy] || "Breakdown";

		return [
			{ id: "group", header: groupByLabel, cell: (item) => displayValue(item.group) },
			{ id: "grantsSuccess", header: "Grants success", cell: (item) => formatNumber(item.grants.success) },
			{ id: "grantsFailed", header: "Grants failed", cell: (item) => formatNumber(item.grants.failed) },
			{ id: "checksSuccess", header: "Checks success", cell: (item) => formatNumber(item.checks.success) },
			{ id: "checksFail", header: "Checks fail", cell: (item) => formatNumber(item.checks.fail) },
			{ id: "uniquePlayers", header: "Unique players", cell: (item) => formatNumber(item.uniquePlayers) },
		];
	}

	getVisibleBreakdown() {
		const { data, breakdownPage, breakdownPerPage } = this.state;
		const start = (breakdownPage - 1) * breakdownPerPage;

		return data.breakdown.slice(start, start + breakdownPerPage);
	}

	render() {
		const { loaded, data, error, appliedFilters, breakdownPage, breakdownPerPage } = this.state;
		const groupByLabel = GROUP_BY_LABELS[appliedFilters.groupBy] || appliedFilters.groupBy;
		const breakdownPeriodLabel = appliedFilters.dateFrom || appliedFilters.dateTo ? `${appliedFilters.dateFrom || "-"} - ${appliedFilters.dateTo || "-"}` : "All time";
		const breakdownPages = Math.max(Math.ceil(data.breakdown.length / breakdownPerPage), 1);
		const visibleBreakdown = this.getVisibleBreakdown();

		return (
			<React.Fragment>
				<SpaceBetween size="m">
					{loaded && (error.text || "").length ? (
						<Alert type="error" header="Error loading VIP FS analytics" dismissible onDismiss={() => this.setState({ error: { text: "", code: 0, timestamp: 0 } })}>
							<Box variant="p">{error.text}</Box>
							<hr />
							<Box variant="p">
								Request error code: {error.code} | Request timestamp: {error.timestamp}
							</Box>
						</Alert>
					) : null}

					{this.renderFilters()}

					<Container header={<Header variant="h2">VIP FS Analytics</Header>}>{this.renderTotals()}</Container>

					<Table
						stickyHeader
						trackBy="group"
						loading={!loaded}
						columnDefinitions={this.getBreakdownColumns()}
						items={visibleBreakdown}
						header={
							<Header variant="h2" counter={`(${data.breakdown.length})`} description={breakdownPeriodLabel}>
								Breakdown by {groupByLabel}
							</Header>
						}
						pagination={
							<Pagination
								disabled={!loaded}
								currentPageIndex={breakdownPage}
								pagesCount={breakdownPages}
								onChange={({ detail }) => this.setState({ breakdownPage: detail.currentPageIndex })}
							/>
						}
						preferences={
							<Preferences
								cancelLabel="Cancel"
								confirmLabel="Confirm"
								pageSizePreference={{ title: "Breakdown rows per page", options: PAGE_SIZE_OPTIONS }}
								preferences={{ pageSize: breakdownPerPage }}
								title="Preferences"
								onConfirm={({ detail }) => {
									const nextPageSize = Number(detail.pageSize);
									localStorage.setItem("freespinsAnalytics.breakdownPerPage", nextPageSize);
									this.setState({ breakdownPerPage: nextPageSize, breakdownPage: 1 });
								}}
							/>
						}
						empty={
							<Box textAlign="center" color="inherit">
								<b>No VIP FS analytics found</b>
								<Box padding={{ bottom: "s" }} variant="p" color="inherit">
									Change period or breakdown filter.
								</Box>
							</Box>
						}
					/>
				</SpaceBetween>
			</React.Fragment>
		);
	}
}

export default VipFreespinsAnalytics;
